import isUndefined from 'lodash-es/isUndefined'
import React, { cloneElement, useState } from 'react'
import type { SortEvent, SortEventWithTag } from 'react-sortable-hoc'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'

import { PhotoThumbnail } from '@components/common/PhotoThumbnail'
import { QuantityBadge } from '@components/common/QuantityBadge'
import { Avatar, ButtonClean, Dropdown, Icon } from '@fourthwall/components'
import { DotsHorizontalIcon, DragIcon } from '@fourthwall/icons'

import { HELPER_CLASS } from './consts'
import * as S from './styled'
import type { ContainerProps, ElementProps, ItemProps, Props } from './types'

const SelectedListElement = SortableElement<ElementProps>(
  ({
    id,
    photoUrl,
    quantity,
    name,
    link,
    rightContent,
    showAvatar,
    isDragged,
    disableSorting,
    onPositionChange,
    onRemove,
  }: ElementProps) => {
    const [dropdownKey, setDropdownKey] = useState(0)
    const handleMouseLeave = () => setDropdownKey((key) => key + 1)

    const renderLeftContent = () => {
      if (showAvatar) {
        return (
          <S.ImageContainer>
            <Avatar id={id} name={name} />
          </S.ImageContainer>
        )
      }

      return (
        <S.ImageContainer>
          <PhotoThumbnail url={photoUrl} size="medium" />
          {quantity && <QuantityBadge quantity={quantity} />}
        </S.ImageContainer>
      )
    }

    const renderRightContent = () => {
      if (rightContent) {
        return <S.RightContent>{rightContent}</S.RightContent>
      }
    }

    return (
      <S.Element
        isDragged={isDragged}
        disableSorting={disableSorting}
        onMouseLeave={handleMouseLeave}
        data-testid="SelectedListElement"
      >
        {!disableSorting && (
          <S.DragHandle title="Drag">
            <Icon component={DragIcon} height={16} />
          </S.DragHandle>
        )}

        {renderLeftContent()}

        <S.Name>
          {link ? <ButtonClean to={link} label={name} withUnderline={false} /> : name}
        </S.Name>

        {renderRightContent()}

        <Dropdown
          key={dropdownKey}
          content={
            <>
              {!disableSorting && (
                <Dropdown.Item label="Change position" onClick={onPositionChange} />
              )}
              <Dropdown.Item label="Remove from collection" onClick={onRemove} />
            </>
          }
          position="bottom-end"
        >
          <S.Handle title="Options" data-testid="SelectedList.Options">
            <Icon component={DotsHorizontalIcon} height={16} />
          </S.Handle>
        </Dropdown>
      </S.Element>
    )
  },
)

const SelectedListItem = ({
  index,
  id,
  photoUrl,
  quantity,
  showAvatar,
  name,
  link,
  rightContent,
  isDragged,
  disableSorting,
  onPositionChange,
  onRemove,
}: ItemProps) => {
  if (isUndefined(index) || isUndefined(isDragged)) {
    return null
  }

  const handleRemove = () => {
    if (onRemove) {
      onRemove(index)
    }
  }

  const handlePositionChange = () => {
    if (onPositionChange) {
      onPositionChange(index)
    }
  }

  return (
    <S.Item>
      <S.Placeholder />
      <SelectedListElement
        index={index}
        id={id}
        photoUrl={photoUrl}
        quantity={quantity}
        showAvatar={showAvatar}
        name={name}
        link={link}
        rightContent={rightContent}
        isDragged={isDragged}
        disableSorting={disableSorting}
        onPositionChange={handlePositionChange}
        onRemove={handleRemove}
      />
    </S.Item>
  )
}

const SelectedListContainer = SortableContainer<ContainerProps>(
  ({ isDragged, disableSorting, children, onPositionChange, onRemove }: ContainerProps) => (
    <div>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return cloneElement<any>(child, {
            index,
            isDragged,
            disableSorting,
            onPositionChange,
            onRemove,
          })
        }
      })}
    </div>
  ),
)

export const SelectedList = ({
  children,
  disableSorting,
  onSortChange,
  onPositionChange,
  onRemove,
}: Props) => {
  const [isDragged, setIsDragged] = useState(false)

  const handleSortStart = () => {
    setIsDragged(true)
  }

  const handleSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    setIsDragged(false)

    if (onSortChange && !disableSorting) {
      onSortChange(oldIndex, newIndex)
    }
  }

  const shouldCancelStart = (e: SortEvent | SortEventWithTag) => {
    if (disableSorting) {
      return true
    }

    if ('tagName' in e.target) {
      return e.target.tagName.toLowerCase() === 'a'
    }

    return false
  }

  return (
    <SelectedListContainer
      isDragged={isDragged}
      disableSorting={disableSorting}
      helperClass={HELPER_CLASS}
      distance={1}
      axis="y"
      shouldCancelStart={shouldCancelStart}
      onSortStart={handleSortStart}
      onSortEnd={handleSortEnd}
      onPositionChange={onPositionChange}
      onRemove={onRemove}
    >
      {children}
    </SelectedListContainer>
  )
}

SelectedList.Item = SelectedListItem
